import { addCloudModeIntegrations } from '@/features/shared/analytics/integrations'
import ShiptAnalytics, {
  type AllAnalyticsEvents,
  getAnonymousId,
  getCampaignInfo,
  getSessionInfo,
} from '@shipt/analytics-member-web'
import { type ShiptEventOptions } from '@shipt/analytics-core'
import { v4 as uuidv4 } from 'uuid'
import { isOnServer } from '@shared/constants/util'
import { useState } from 'react'
import {
  getAllUserTraits,
  getCPATraits,
  getGroupProperties,
  getPageData,
  shouldDisableAnalytics,
} from '@/features/shared/analytics/utils'
import {
  handleMarvAdviceCookieExpiration,
  MARV_ADVICE_BY_CAMPAIGN,
} from '@/features/shared/utils/marv'
import { getCookie } from '@/features/shared/utils/cookies'
import { getUser } from '@/features/account/services/User/utils'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { getConsentObject, getIsConsentedToAnalytics } from '@/lib/transcend/utils'
import { queueOrPushEvent } from '@/features/shared/analytics/google'

export const getTrackOptions = (
  traits?: Record<string, unknown>,
): Pick<ShiptEventOptions, 'anonymousId' | 'context' | 'integrations' | 'userId' | 'isQueued'> => {
  const userData = getUser(getQueryClient())
  const { sessionId } = getSessionInfo()
  const { unverified_is_comet } = getCPATraits()
  const cpaTraits = {
    ...(unverified_is_comet && { unverified_is_comet }),
  }

  return {
    anonymousId: getAnonymousId() ?? '',
    context: {
      linkKey: uuidv4(),
      eventId: uuidv4(),
      traits: {
        ...traits,
        ...getAllUserTraits(userData),
        ...cpaTraits,
      },
      campaign: getCampaignInfo(getPageData()),
      sessionId,
      consent: getConsentObject(),
      ...getGroupProperties(),
    },
    integrations: addCloudModeIntegrations(),
    userId: userData?.id ? String(userData.id) : null,
    isQueued: !getIsConsentedToAnalytics(),
  }
}

export const track = (
  event: AllAnalyticsEvents & { eventOptions?: Partial<ShiptEventOptions> },
) => {
  const { eventName, properties, eventOptions } = event
  if (isOnServer() || shouldDisableAnalytics()) return
  handleMarvAdviceCookieExpiration()
  const options = {
    ...getTrackOptions({ recruitics_viewer_id: window.rx?.getViewerId() }),
    ...eventOptions,
  }
  const marvCookieValue = getCookie(MARV_ADVICE_BY_CAMPAIGN)

  const eventProperties: AllAnalyticsEvents['properties'] = {
    ...properties,
    ...(marvCookieValue && {
      marv_advice_by_campaign: JSON.stringify(marvCookieValue),
    }),
  }
  const eventId = options.context.eventId ?? ''

  queueOrPushEvent({ ...event, eventId })

  ShiptAnalytics.track(eventName, eventProperties, options)
}

export const useTrackingQueue = () => {
  const [eventQueue, setEventQueue] = useState<AllAnalyticsEvents[]>([])
  const trackQueue = (event: AllAnalyticsEvents) => {
    if (isOnServer()) return
    setEventQueue([...eventQueue, event])
  }
  const sendAllQueuedEvents = () => {
    if (isOnServer() || !eventQueue.length) return
    while (eventQueue.length) {
      const event = eventQueue.shift()
      if (event) track(event)
    }
  }

  return {
    eventQueue,
    trackQueue,
    sendAllQueuedEvents,
  }
}
